import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, Animated, TouchableOpacity } from 'react-native';

import { Box } from '@components/Restyle';
import Text from '@components/Restyle/Text';
import Icon from '@components/shared/Icon/Icon';
import theme from '@themes/theme';

type TaskCreatedToastProps = {
  taskName: string;
  duration: number;
  onPress?: () => void;
  message?: string;
  // Extra line under the task name, e.g. that a copy dropped its
  // dependencies (TA-11209).
  note?: string;
  isFromProject?: boolean;
};

export const TaskCreatedToast: React.FC<TaskCreatedToastProps> = ({
  taskName,
  duration,
  onPress,
  message,
  note,
  isFromProject = false,
}) => {
  const progressAnim = useRef(new Animated.Value(0)).current;
  const { t } = useTranslation();
  useEffect(() => {
    Animated.timing(progressAnim, {
      toValue: 1,
      duration: duration,
      useNativeDriver: false,
    }).start();
  }, [progressAnim, duration]);

  const handlePress = () => {
    onPress?.();
  };

  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={handlePress}
      accessibilityLabel={
        isFromProject
          ? t('models:customToast.projectCreated')
          : t('models:tasks.create.taskCreated')
      }
      style={styles.toastContainer}
      testID='task-created-toast'>
      <Box style={styles.content}>
        <Box style={styles.iconContainer}>
          <Icon name='Selected' height={16} width={16} color='white' />
        </Box>
        <Box style={styles.textContainer}>
          <Text variant='labelEmphasized' style={styles.titleText}>
            {message}
          </Text>
          <Text variant='labelSmall' style={styles.taskNameText}>
            {taskName}
          </Text>
          {!!note && (
            <Text
              variant='labelSmall'
              style={styles.noteText}
              testID='task-created-toast-note'>
              {note}
            </Text>
          )}
        </Box>
        <Box style={styles.arrowIcon}>
          <Icon name='ArrowRight' height={24} width={24} color='white' />
        </Box>
      </Box>
      <Animated.View
        testID='task-created-toast-progress'
        style={[
          styles.progressBar,
          {
            width: progressAnim.interpolate({
              inputRange: [0, 1],
              outputRange: ['0%', '100%'],
            }),
          },
        ]}
      />
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  // Figma (node 154:31485): black card, 8px radius, overflow-clip — the
  // highlight bar is a normal-flow sibling below the content row, not an
  // absolutely-positioned overlay, so the outer overflow+radius clips its
  // bottom corners to match instead of needing its own corner radii.
  toastContainer: {
    backgroundColor: 'black',
    borderRadius: theme.borderRadii.xs,
    overflow: 'hidden',
    // Without an explicit width the toast library's message wrapper let this
    // shrink to content on Android, making the progress bar look too narrow
    // (TA-10311 QA on Android). Not yet confirmed whether iOS needs this too.
    width: '100%',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: theme.spacing.xs,
    paddingHorizontal: theme.spacing.s,
  },
  iconContainer: {
    marginRight: theme.spacing.s,
  },
  arrowIcon: {
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: theme.spacing.m,
  },
  textContainer: {
    flex: 1,
    justifyContent: 'center',
  },
  titleText: {
    color: 'white',
    fontWeight: 'bold',
  },
  taskNameText: {
    color: 'white',
  },
  noteText: {
    color: 'white',
    marginTop: theme.spacing.xxxs,
  },
  progressBar: {
    height: 4,
    backgroundColor: theme.colors.greenBrand,
  },
});
